import type { GetTransactionsResponse } from "../../../../types/transactions";
import { parseApiDateTime } from "../../../../utils/timeUtils";
import { emptyRow, ServiceRow } from "./bookingTypes";

export interface TransactionSeed {
  rows: ServiceRow[];
  /** Salon-local day the session opens on ("YYYY-MM-DD"). */
  day: string;
  clientUuid: string;
}

/**
 * Seed a reschedule ("edit") or rebook session from an existing transaction.
 *
 * - start_dt is a UTC instant: parsed with parseApiDateTime (UTC → salon tz).
 * - EDIT keeps each service's time and carries the source booked-service +
 *   line-item uuids for the in-place PUT; REBOOK leaves the time blank so the
 *   user re-picks it.
 * - `baseDuration` is the stored base_duration_mins (unadjusted). Existing
 *   adjustment answers are carried over (each sub line item's source_id is
 *   the selected option id) and their duration_delta_mins sum is the row's
 *   `adjustmentDuration`: shown in the row's length, never sent. EDIT counts
 *   every sub line item (they stay on the appointment) and remembers the
 *   seeded option ids; REBOOK counts those with an option id (the ones the
 *   new appointment gets).
 * - The session day is the appointment's first service day (salon tz), or
 *   today when the transaction has no booked services.
 */
export const seedFromTransaction = (
  txn: GetTransactionsResponse,
  mode: "edit" | "rebook",
  timezone: string,
  now: Date,
): TransactionSeed => {
  const booked = txn.line_items?.booked_services || [];
  const rows: ServiceRow[] = booked.map((s, i) => {
    const start = s.start_dt ? parseApiDateTime(s.start_dt, timezone) : null;
    const subItems = s.sub_line_items || [];
    const adjustmentOptionIds = subItems
      .map((sli) => sli.source_id)
      .filter((id): id is string => !!id);
    const carried =
      mode === "edit" ? subItems : subItems.filter((sli) => !!sli.source_id);
    return {
      uid: i + 1,
      salonServiceId: s.salon_service_id || "",
      employeeId: s.staff_id || "",
      date: null,
      time: mode === "edit" && start ? start.format("HH:mm") : "",
      baseDuration: s.base_duration_mins ?? 0,
      processingTime: s.processing_time_mins ?? 0,
      finishingTime: s.finishing_time_mins ?? 0,
      bufferTime: s.buffer_time_mins ?? 0,
      price: s.unit_price ?? 0,
      adjustmentOptionIds,
      adjustmentDuration: carried.reduce(
        (sum, sli) => sum + (sli.duration_delta_mins || 0),
        0,
      ),
      ...(mode === "edit"
        ? {
            sourceBookedServiceId: s.id,
            sourceLineItemId: s.line_item_id,
            sourceAdjustmentOptionIds: adjustmentOptionIds,
          }
        : {}),
    };
  });
  const firstStart = booked.find((s) => s.start_dt)?.start_dt;
  const day = firstStart
    ? parseApiDateTime(firstStart, timezone).format("YYYY-MM-DD")
    : parseApiDateTime(now.toISOString(), timezone).format("YYYY-MM-DD");
  return {
    rows: rows.length ? rows : [emptyRow(1, null)],
    day,
    clientUuid: txn.client?.id || "",
  };
};

/**
 * Each booked service's start relative to the first one (minutes), in
 * booked-service order (the order seedFromTransaction builds rows in). A
 * rebook keeps the services' spacing and moves them together.
 */
export const rebookStartOffsets = (
  txn: GetTransactionsResponse,
  timezone: string,
): number[] => {
  const booked = txn.line_items?.booked_services || [];
  const firstStart = booked.find((s) => s.start_dt)?.start_dt;
  if (!firstStart) return booked.map(() => 0);
  const first = parseApiDateTime(firstStart, timezone).valueOf();
  return booked.map((s) =>
    s.start_dt
      ? Math.round(
          (parseApiDateTime(s.start_dt, timezone).valueOf() - first) / 60000,
        )
      : 0,
  );
};

/** The source appointment's first service start, salon wall-clock "HH:mm". */
export const sourceStartTime = (
  txn: GetTransactionsResponse,
  timezone: string,
): string => {
  const firstStart = (txn.line_items?.booked_services || []).find(
    (s) => s.start_dt,
  )?.start_dt;
  return firstStart
    ? parseApiDateTime(firstStart, timezone).format("HH:mm")
    : "";
};
